import { Period } from 'screens/Analytics/constants'
import { MetricSnapshot } from 'hooks/queries/externalIntegrations/types'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { last } from 'lodash'

dayjs.extend(isSameOrBefore)

export const getRate = (diff: number) => {
  if (diff === 0) return 'neutral'
  if (diff > 0) return 'positive'
  if (diff < 0) return 'negative'

  return undefined
}

export const mapPeriodToPreviousPeriod = (period: Period) => {
  switch (period) {
    case Period.LastWeek:
      return Period.Last2Weeks
    case Period.LastMonth:
      return Period.Last2Months
    case Period.Last3Months:
      return Period.Last6Months
    case Period.Last6Months:
      return Period.LastYear
    default:
      return period
  }
}

export const mapPeriodTodays = (period: Period) => {
  switch (period) {
    case Period.LastWeek:
      return 7
    case Period.Last2Weeks:
      return 14
    case Period.LastMonth:
      return 30
    case Period.Last2Months:
      return 60
    case Period.Last3Months:
      return 90
    case Period.Last6Months:
      return 180
    case Period.LastYear:
      return 365
    default:
      return 0
  }
}

export const getPeriodToLabel = (period: Period) => {
  switch (period) {
    case Period.LastWeek:
      return '7 days'
    case Period.Last2Weeks:
      return '14 days'
    case Period.LastMonth:
      return 'month'
    case Period.Last2Months:
      return '2 months'
    case Period.Last3Months:
      return '3 months'
    case Period.Last6Months:
      return '6 months'
    case Period.LastYear:
      return 'year'
    default:
      return ''
  }
}

export const lastSnapshotDate = (snapshots: MetricSnapshot[]) => {
  return !!last(snapshots)?.recorded_at ? dayjs(last(snapshots)?.recorded_at).endOf('day') : dayjs().endOf('day')
}

export const sumMetricValues = (snapshots: MetricSnapshot[]) =>
  snapshots.reduce((acc, snapshot) => acc + snapshot.metric_value, 0)

export const calculateRollingPeriodValue = (snapshots: MetricSnapshot[], period: Period) => {
  const totalDays = mapPeriodTodays(period)
  const endDate = lastSnapshotDate(snapshots)
  const startDate = endDate.subtract(totalDays - 1, 'day').startOf('day')

  // Create a map of snapshots by date (YYYY-MM-DD) for fast lookup
  const snapshotMap = new Map<string, MetricSnapshot>()
  snapshots.forEach((snapshot) => {
    const dateKey = dayjs(snapshot.recorded_at).format('YYYY-MM-DD')
    snapshotMap.set(dateKey, snapshot)
  })

  // Generate array with a snapshot for every day in the period
  const completeSnapshots: MetricSnapshot[] = []
  let currentDate = startDate

  while (currentDate.isSameOrBefore(endDate, 'day')) {
    const dateKey = currentDate.format('YYYY-MM-DD')
    const existingSnapshot = snapshotMap.get(dateKey)

    if (existingSnapshot) {
      completeSnapshots.push(existingSnapshot)
    } else {
      // Create an empty snapshot for this date
      const emptySnapshot: MetricSnapshot = {
        id: 0,
        metric_name: snapshots[0]?.metric_name || '',
        metric_value: 0,
        recorded_at: currentDate.toISOString(),
        metric_diffs: [],
      }
      completeSnapshots.push(emptySnapshot)
    }

    currentDate = currentDate.add(1, 'day')
  }

  return completeSnapshots
}

export const getDiffedSnapshots = (snapshots: MetricSnapshot[], applyImprovements: boolean) => {
  const diffedSnapshots = snapshots.map((snapshot, index) => {
    if (snapshots.length === 1 && applyImprovements) return snapshot
    if (index === 0) return { ...snapshot, metric_value: 0 }

    const previousValue = snapshots[index - 1].metric_value
    if (snapshot.metric_value < previousValue) return { ...snapshot, metric_value: 0 }

    return {
      ...snapshot,
      metric_value: snapshot.metric_value - previousValue,
    }
  })

  return diffedSnapshots.length === 1 || !applyImprovements ? diffedSnapshots : diffedSnapshots.slice(1)
}

export const getChartColor = (rate: 'positive' | 'negative' | 'neutral' | undefined) => {
  if (rate === 'positive') {
    return '#20A14F'
  } else if (rate === 'negative') {
    return '#AE2222'
  } else if (rate === 'neutral') {
    return '#B8BFCC'
  } else {
    return undefined
  }
}
